<template>
  <BlockFieldset class="project-time-tracking-summary">
    <template v-if="timeTrackingSummaryRows.length">
      <table class="table table-sm mb-0 w-100">
        <thead>
          <tr>
            <th>{{ $t('timeTrackingType') }}</th>
            <th class="text-right">{{ $t('timeTrackingActualHours') }}</th>
            <th class="text-right">{{ $t('timeTrackingExpectedHours') }}</th>
            <th class="text-right">{{ $t('timeTrackingDeltaHours') }}</th>
            <th class="text-right">{{ $t('timeTrackingEntries') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in timeTrackingSummaryRows" :key="row.type">
            <td>
              <b-badge :variant="getAppointmentTypeColor(row.type)">
                {{ $t(row.type) }}
              </b-badge>
            </td>
            <td class="text-right">{{ $n(row.actualHours, 'decimal') }}</td>
            <td class="text-right">{{ formatOptionalHours(row.expectedHours) }}</td>
            <td class="text-right">{{ formatOptionalHours(row.deltaHours) }}</td>
            <td class="text-right">{{ row.entryCount }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th>{{ $t('timeTrackingTotalHours') }}</th>
            <th class="text-right">{{ $n(timeTrackingTotalHours, 'decimal') }}</th>
            <th class="text-right">{{ $n(timeTrackingTotalExpectedHours, 'decimal') }}</th>
            <th class="text-right">{{ $n(timeTrackingTotalDeltaHours, 'decimal') }}</th>
            <th></th>
          </tr>
        </tfoot>
      </table>
    </template>
    <template v-else>
      <div class="text-muted">{{ $t('timeTrackingNoEntries') }}</div>
    </template>
  </BlockFieldset>
</template>

<script>
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import { getAppointmentTypeColor } from '@/helpers/colors';

const SECONDS_PER_HOUR = 3600;

/**
 * Read-only block project-time-tracking-summary ("Auswertung Zeiterfassung", see feature page
 * projekt-technische-pruefung): actual, expected and delta hours per appointment type. `project`
 * is the block data (`Project.blocks.timeTrackingSummary`) or the unsaved project. The summary is
 * stored on the project and recomputed by the server on every project save. No controls, so no
 * save and no read-only handling beyond `BlockFieldset`.
 */
export default {
  name: 'ProjectTimeTrackingSummary',
  components: {
    BlockFieldset,
  },
  props: {
    project: { type: Object, required: true },
  },
  computed: {
    timeTrackingSummaryItems() {
      return this.project.timeTrackingSummary?.items || [];
    },
    timeTrackingSummaryRows() {
      return this.timeTrackingSummaryItems.map((item) => ({
        type: item.type,
        actualHours: this.secondsToHours(item.actualSeconds),
        expectedHours:
          item.expectedSeconds === null || item.expectedSeconds === undefined
            ? null
            : this.secondsToHours(item.expectedSeconds),
        deltaHours:
          item.deltaSeconds === null || item.deltaSeconds === undefined
            ? null
            : this.secondsToHours(item.deltaSeconds),
        entryCount: item.entryCount ?? 0,
      }));
    },
    timeTrackingTotalHours() {
      return this.secondsToHours(this.project.timeTrackingSummary?.totalSeconds);
    },
    timeTrackingTotalExpectedHours() {
      return this.secondsToHours(this.sumOfItemSeconds('expectedSeconds'));
    },
    timeTrackingTotalDeltaHours() {
      return this.secondsToHours(this.sumOfItemSeconds('deltaSeconds'));
    },
  },
  methods: {
    getAppointmentTypeColor,
    /** Sum of one seconds field over all items; a missing value counts as 0. */
    sumOfItemSeconds(secondsField) {
      return this.timeTrackingSummaryItems.reduce(
        (sum, item) =>
          sum +
          (item[secondsField] === null || item[secondsField] === undefined
            ? 0
            : Number(item[secondsField]) || 0),
        0
      );
    },
    secondsToHours(seconds) {
      if (seconds === null || seconds === undefined) return '-';
      const hours = Number(seconds) / SECONDS_PER_HOUR;
      if (Number.isNaN(hours)) return '-';
      return hours.toFixed(2);
    },
    formatOptionalHours(hours) {
      return this.$n(hours, 'decimal') ?? '-';
    },
  },
};
</script>
